'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'

import { AnalyticsBrowser } from '@segment/analytics-next'
import type { clarity as Clarity } from 'clarity-js/types'

import { appIdAndKey } from '@env/appIdAndKey'
import { thirdPartyAppsEnv } from '@env/thirdPartyAppsEnv'
import { datadogEnv } from '@env/datadogEnv'

import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'

import { shouldReportLogToDatadog } from '@utils/dataDogUtils'

export type ThirdPartyContext = {
  segment: AnalyticsBrowser | undefined
  setSegment: React.Dispatch<React.SetStateAction<AnalyticsBrowser | undefined>>
  clarity: typeof Clarity | undefined
  setClarity: React.Dispatch<React.SetStateAction<typeof Clarity | undefined>>
}

const defaultContext = {
  segment: undefined,
  setSegment: () => {},
  clarity: undefined,
  setClarity: () => {}
}

export const ThirdPartyContext = createContext<ThirdPartyContext>(defaultContext)

export default function ThirdPartyContextProvider({ children }: { children: React.ReactNode }) {
  const [segment, setSegment] = useState<AnalyticsBrowser | undefined>(undefined)
  const [clarity, setClarity] = useState<typeof Clarity | undefined>(undefined)
  const datadogSite = 'datadoghq.com'
  const datadogService = 'boto-client'

  useEffect(() => {
    const loadAnalytics = async () => {
      const analytics = AnalyticsBrowser.load({
        cdnURL: thirdPartyAppsEnv.NEXT_PUBLIC_SEGMENT_CDN_URL,
        writeKey: appIdAndKey.NEXT_PUBLIC_SEGMENT_WRITE_KEY
      })

      setSegment(analytics)
    }

    loadAnalytics()

    const loadClarity = async () => {
      const clarity = await import('clarity-js').then(clarity => clarity.clarity)
      await clarity.start({
        projectId: thirdPartyAppsEnv.NEXT_PUBLIC_CLARITY_ID,
        upload: 'https://m.clarity.ms/collect',
        track: true,
        content: true
      })

      setClarity(clarity)
    }

    loadClarity()

    const initDatadogRum = () => {
      const applicationId = datadogEnv.NEXT_PUBLIC_ASAAS_DATADOG_CLIENT_RUM_APP_ID

      if (!applicationId) return

      if (!datadogEnv.NEXT_PUBLIC_ASAAS_DATADOG_CLIENT_RUM_TOKEN) return

      if (datadogRum?.getInitConfiguration()?.applicationId === applicationId) return

      datadogRum.init({
        applicationId: applicationId,
        clientToken: datadogEnv.NEXT_PUBLIC_ASAAS_DATADOG_CLIENT_RUM_TOKEN,
        site: datadogSite,
        service: datadogService,
        env: process.env.NODE_ENV,
        version: process.env.NEXT_PUBLIC_RELEASE_VERSION,
        sessionSampleRate: 5,
        sessionReplaySampleRate: 0,
        trackResources: true,
        trackLongTasks: true,
        trackUserInteractions: true,
        defaultPrivacyLevel: 'mask-user-input'
      })
    }

    initDatadogRum()

    const initDatadogLogs = () => {
      if (!datadogEnv.NEXT_PUBLIC_ASAAS_DATADOG_CLIENT_TOKEN) return

      if (datadogLogs?.getInitConfiguration()?.clientToken === datadogEnv.NEXT_PUBLIC_ASAAS_DATADOG_CLIENT_TOKEN) return

      datadogLogs.init({
        clientToken: datadogEnv.NEXT_PUBLIC_ASAAS_DATADOG_CLIENT_TOKEN,
        site: datadogSite,
        service: datadogService,
        forwardErrorsToLogs: true,
        sessionSampleRate: 100,
        silentMultipleInit: true,
        forwardConsoleLogs: 'all',
        version: process.env.NEXT_PUBLIC_RELEASE_VERSION,
        beforeSend: event => shouldReportLogToDatadog(event.message)
      })
    }

    initDatadogLogs()
  }, [])

  return (
    <ThirdPartyContext.Provider value={{ segment, setSegment, clarity, setClarity }}>
      {children}
    </ThirdPartyContext.Provider>
  )
}

export function useThirdPartyContext() {
  return useContext(ThirdPartyContext)
}
